import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { PUBLIC_URL } from "@/lib/env";
import { toast } from "sonner";

const DEVICE_KEY = "rsi_app_device_id";
const PROFILE_KEY = "rsi_app_subscription_profile";
const SEEN_KEY = "rsi_app_seen_notifications";
const PUBLIC_BASE = PUBLIC_URL;
const REQUEST_DEDUPE_MS = 5000;
const inFlightNotificationRequests = new Map();

function withPublicBase(path) {
  if (!path) return `${PUBLIC_BASE}/app`;
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${PUBLIC_BASE}${normalized}`;
}

function getDeviceId() {
  const existing = localStorage.getItem(DEVICE_KEY);
  if (existing) return existing;
  const next = `device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(DEVICE_KEY, next);
  return next;
}

function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "web";
}

function detectStandalone() {
  return window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
}

function readProfile() {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    return raw
      ? JSON.parse(raw)
      : {
          customer_name: "",
          email: "",
          phone: "",
          allow_new_products: true,
          allow_shipment_updates: true,
          allow_delivery_updates: true,
          allow_order_updates: true,
          allow_promotions: false,
          allow_email: true,
          allow_whatsapp: false,
          tracking_ids: [],
          order_numbers: [],
          interested_categories: [],
        };
  } catch {
    return {
      customer_name: "",
      email: "",
      phone: "",
      allow_new_products: true,
      allow_shipment_updates: true,
      allow_delivery_updates: true,
      allow_order_updates: true,
      allow_promotions: false,
      allow_email: true,
      allow_whatsapp: false,
      tracking_ids: [],
      order_numbers: [],
      interested_categories: [],
    };
  }
}

function readSeenIds() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveSeenIds(ids) {
  localStorage.setItem(SEEN_KEY, JSON.stringify(ids.slice(0, 200)));
}

function getNotificationsOnce(queryParams) {
  const now = Date.now();
  const cached = inFlightNotificationRequests.get(queryParams);
  if (cached && now - cached.startedAt < REQUEST_DEDUPE_MS) {
    return cached.promise;
  }
  const promise = api
    .get(`/app/notifications?${queryParams}`)
    .then((response) => response.data)
    .finally(() => {
      const latest = inFlightNotificationRequests.get(queryParams);
      if (latest?.promise === promise) {
        inFlightNotificationRequests.delete(queryParams);
      }
    });
  inFlightNotificationRequests.set(queryParams, {
    promise,
    startedAt: now,
  });
  return promise;
}

export default function useAppNotifications({ trackingId = "", orderNumber = "" } = {}) {
  const [profile, setProfile] = useState(() => readProfile());
  const [permission, setPermission] = useState(() =>
    typeof Notification === "undefined" ? "default" : Notification.permission || "default"
  );
  const deviceId = useMemo(() => getDeviceId(), []);

  useEffect(() => {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  }, [profile]);

  const saveSubscriptionMut = useMutation({
    mutationFn: async (payload) => (await api.post("/app/subscriptions", payload)).data,
    onSuccess: () => toast.success("Notification preferences saved"),
    onError: (error) => toast.error(formatApiError(error)),
  });

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("device_id", deviceId);
    if (profile.email) params.set("email", profile.email);
    if (trackingId) params.set("tracking_id", trackingId);
    if (orderNumber) params.set("order_number", orderNumber);
    params.set("limit", "40");
    return params.toString();
  }, [deviceId, orderNumber, profile.email, trackingId]);

  const notificationsQuery = useQuery({
    queryKey: ["app-notifications", queryParams],
    queryFn: async () => {
      try {
        return await getNotificationsOnce(queryParams);
      } catch (error) {
        throw error;
      }
    },
    refetchInterval: 45_000,
    staleTime: 15_000,
  });

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && !notificationsQuery.isFetching) {
        notificationsQuery.refetch();
      }
    };
    window.addEventListener("focus", handleVisibility);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", handleVisibility);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [notificationsQuery]);

  useEffect(() => {
    const items = notificationsQuery.data || [];
    const seenIds = readSeenIds();
    const unseen = items.filter((item) => item?.id && !seenIds.includes(item.id));
    if (!unseen.length) return;
    saveSeenIds([...unseen.map((item) => item.id), ...seenIds]);

    if (permission !== "granted") return;
    unseen.slice(0, 3).forEach(async (item) => {
      const options = {
        body: item.body,
        icon: withPublicBase("/icon-192.png"),
        badge: withPublicBase("/icon-192.png"),
        data: { url: withPublicBase(item.cta_url || "/app") },
      };
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration?.showNotification) {
          registration.showNotification(item.title, options);
        } else if (typeof Notification !== "undefined") {
          new Notification(item.title, options);
        }
      } catch {
        // Notification display should not break the app.
      }
    });
  }, [notificationsQuery.data, permission]);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Notifications are not supported on this device");
      return "default";
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      try {
        await saveSubscriptionMut.mutateAsync({
          ...profile,
          device_id: deviceId,
          platform: detectPlatform(),
          standalone: detectStandalone(),
          notification_permission: result,
        });
      } catch {
        // The preference toast is already handled by the mutation.
      }
      return result;
    } catch {
      return permission;
    }
  };

  const saveSubscription = async (nextProfile) => {
    const payload = {
      ...nextProfile,
      device_id: deviceId,
      platform: detectPlatform(),
      standalone: detectStandalone(),
      notification_permission: permission,
    };
    setProfile(nextProfile);
    await saveSubscriptionMut.mutateAsync(payload);
  };

  return {
    deviceId,
    permission,
    profile,
    setProfile,
    notifications: notificationsQuery.data || [],
    notificationsLoading: notificationsQuery.isLoading,
    notificationsRefreshing: notificationsQuery.isFetching,
    refreshNotifications: notificationsQuery.refetch,
    saveSubscription,
    saveSubscriptionMut,
    requestPermission,
  };
}
